import {NEVER, Observable, of} from 'rxjs';
import {mergeMap} from 'rxjs/operators';

import {Injectable} from '@angular/core';

import * as APIM from '@common/utils/dist/models/api-messages.js';
import * as PL2 from '@common/utils/dist/models/models-pl2.js';
import {EntryUtilsPL2 as EU} from '@common/utils/dist/utils/entry-utils-pl2.js';

import {UtilsPL2 as U} from '@common/utils/dist/utils/utils-pl2.js';

import {WSMessageReceiverService} from '@services/ws-message-receiver.service';

import {ErrorReportStore} from '@stores/error-report.store';
import {EventTrackingStore} from '@stores/event-tracking.store';
import {EntryStore} from '@stores/entry.store';
import {PendingOperation} from '@stores/entry-store-automaton/operation-state-machine';

import {EntryStoreUtils as ESU} from '@utils/entry-store-utils';
import {UnknownError} from '@utils/errors';
import {APIError} from '@utils/api-errors';
import {isEntryChange, isEntryQuery} from '@utils/event-utils';

import {
  WebsocketMessageProcessor,
  WSMessageProcessorInput,
  WSMessageProcessorOutput,
} from './websocket-message-processor';

@Injectable()
export class ErrorMessageProcessor implements WebsocketMessageProcessor {
  private readonly _maxRetries = 3;

  constructor(
    private wsMessageReceiverService: WSMessageReceiverService,
    private errorReportStore: ErrorReportStore,
    private entryStore: EntryStore,
    private eventTrackingStore: EventTrackingStore,
  ) {}

  processMessage(
    params: WSMessageProcessorInput,
  ): Observable<WSMessageProcessorOutput> {
    const msg = params.msg;
    if (msg.int === APIM.EventIntent.SomethingWentWrong) {
      return this.wsMessageReceiverService
        .receive<APIM.NotifyRequest>(msg)
        .pipe(
          mergeMap((request) => {
            // TODO fix services: remove payload container object
            const error = APIError.buildError({
              message: msg.err?.message ?? (msg.err as any)?.payload?.message,
              status: msg.err?.code ?? (msg.err as any)?.payload?.code,
            });

            const erroneousEntries = (
              (Array.isArray(request.c)
                ? request.c
                : []) as PL2.AnyPartialEntryWithId[]
            ).filter(
              (e) =>
                EU.isEntryLike(e) &&
                ESU.isKnownEntry(e, this.entryStore.state()),
            );

            if (this._shouldRetry(msg, error)) {
              return params.wsAPI?.retry(msg, 3000);
              // Do not send any reports or report errors back to the websocket service
              // on retry
            } else if (this._isActionableError(msg)) {
              if (U.isEmpty(erroneousEntries)) {
                this._sendErrorReport(error);
              } else {
                this.entryStore.advanceCachedEntryState(
                  erroneousEntries,
                  PendingOperation.Ignore,
                  error,
                );
              }
            }

            params.wsAPI?.error(msg, request);
            return of(true);
          }),
        );
    }
    return NEVER;
  }

  private _isActionableError(envelope: APIM.Event): boolean {
    const activeEvent = this.eventTrackingStore.state()[envelope.id];
    // Only ignore if the failure is a result of an event in this session.
    // Otherwise we might remove stuff we don't want to
    if (U.isEmpty(activeEvent)) {
      return false;
    }
    return activeEvent?.retryCount >= this._maxRetries;
  }

  private _shouldRetry(envelope: APIM.Event, error: APIError.Error): boolean {
    const activeEvent = this.eventTrackingStore.state()[envelope.id];
    if (U.isEmpty(activeEvent)) {
      return false;
    }
    // For now only retry if it's an entry change or entry query event
    if (
      !isEntryChange(activeEvent?.event?.int) &&
      !isEntryQuery(activeEvent?.event?.int)
    ) {
      return false;
    }

    if (error.status === 403) {
      return false;
    }
    return activeEvent.retryCount < this._maxRetries;
  }

  private _sendErrorReport(error: APIError.Error) {
    const errorReport = {
      code: error.status ?? 500,
      message: error.message ?? UnknownError,
    };
    this.errorReportStore.addReport(errorReport);
  }
}
